import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, ShieldAlert, Clock, Download, Undo2, Link2, Copy, Check } from "lucide-react";

/**
 * Danger Zone (Iter-193) — 24-hour cool-down delete + restore link + export ZIP.
 * Only the Business Owner sees it. Requires typed-name + password to arm.
 * After arming, the section flips to a live cool-down banner with:
 *   • Countdown to hard-delete
 *   • "Cancel delete" button (aborts the pending delete cleanly)
 *   • "Download data snapshot" button (server prepared a ZIP)
 *   • Copyable restore link (works up to 30-day hard-purge)
 */
export default function DeleteCompanySection({ org, currentRole, onOrgPatch }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (currentRole !== "business_owner") return null;

  const pending = !!org?.is_pending_delete;
  const nameMatches = confirm.trim() === (org?.name || "").trim();
  const canSubmit = !!password && nameMatches && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api.delete("/organization/self", {
        data: { password, confirm_name: confirm, reason: reason || null },
      });
      toast.success(`Delete scheduled — 24 hour cool-down started`, { duration: 5000 });
      // Patch parent so it re-renders in cool-down mode with restore link
      onOrgPatch && onOrgPatch({
        is_pending_delete: true,
        hard_delete_at: r.data.hard_delete_at,
        restore_token: r.data.restore_token,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const cancelDelete = async () => {
    if (!window.confirm("Cancel the pending delete? Your company will stay live.")) return;
    setBusy(true);
    try {
      await api.post("/organization/cancel-delete");
      toast.success("Delete cancelled — company is live");
      onOrgPatch && onOrgPatch({ is_pending_delete: false, hard_delete_at: null, restore_token: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const downloadExport = async () => {
    try {
      const r = await api.get("/organization/pre-delete-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `zenith360_export_${(org?.id || "").slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot not ready yet — try again in a moment");
    }
  };

  const restoreUrl = pending && org?.restore_token
    ? `${window.location.origin}/restore?org=${org.id}&token=${org.restore_token}` : "";
  const copyRestore = async () => {
    try {
      await navigator.clipboard.writeText(restoreUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  // ---- Cool-down banner --------------------------------------------------
  if (pending) {
    const hoursLeft = org.hard_delete_at
      ? Math.max(0, Math.round((new Date(org.hard_delete_at) - Date.now()) / 3600000))
      : 24;
    return (
      <div className="border-t border-red-300/50 pt-4 mt-6" data-testid="delete-cooldown-banner">
        <div className="rounded-sm border border-red-300 bg-red-50/70 p-4 space-y-3">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
            <Clock className="w-4 h-4" /> Delete pending — {hoursLeft} hour(s) left
          </div>
          <div className="text-xs text-muted-foreground leading-relaxed">
            <b>{org?.name}</b> is scheduled for hard-delete at{" "}
            <span className="font-mono">{new Date(org.hard_delete_at).toLocaleString()}</span>.
            Cancel any time before then — no data will be lost.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={cancelDelete} disabled={busy}
                      className="rounded-sm gap-1.5 text-emerald-700"
                      data-testid="cancel-pending-delete-btn">
              <Undo2 className="w-3.5 h-3.5" /> Cancel delete
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExport}
                      className="rounded-sm gap-1.5"
                      data-testid="download-pre-delete-export-btn">
              <Download className="w-3.5 h-3.5" /> Download data snapshot (ZIP)
            </Button>
          </div>
          {restoreUrl && (
            <div className="rounded-sm border bg-white p-2.5">
              <Label className="text-[10px] uppercase font-mono text-muted-foreground flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Restore link (works for 30 days)
              </Label>
              <div className="mt-1 flex gap-1">
                <Input readOnly value={restoreUrl} className="rounded-sm font-mono text-[11px] h-8"
                        data-testid="restore-link-input" />
                <Button size="sm" variant="outline" onClick={copyRestore}
                        className="rounded-sm h-8 gap-1"
                        data-testid="copy-restore-link-btn">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Bookmark this URL — anyone with it can restore your company inside the 30-day window (no login needed).
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Danger-zone initial state ----------------------------------------
  return (
    <div className="border-t border-red-300/50 pt-4 mt-6" data-testid="danger-zone">
      <div className="text-[10px] font-mono uppercase tracking-widest text-red-600 flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5" /> Danger zone
      </div>
      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap rounded-sm border border-red-200 bg-red-50/40 p-4">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-medium text-foreground">Delete this company</div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Schedules <b>{org?.name || "this company"}</b> for hard-delete after a
            24-hour cool-down. A data-snapshot ZIP is prepared automatically. You
            (or anyone with the restore link) can undo any time in the next 30 days.
          </div>
        </div>
        <Button variant="destructive"
                  onClick={() => { setOpen(true); setPassword(""); setConfirm(""); setReason(""); }}
                  className="rounded-sm gap-1.5"
                  data-testid="delete-company-btn">
          <Trash2 className="w-3.5 h-3.5" /> Delete company
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="delete-company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" /> Schedule delete for "{org?.name}"?
            </DialogTitle>
            <DialogDescription>
              After you confirm, a 24-hour cool-down starts. You can cancel any time
              before it elapses. A data snapshot will be prepared in the background.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Type the company name to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      placeholder={org?.name || ""}
                      className="rounded-sm mt-1 font-mono"
                      data-testid="delete-company-confirm-name" />
              {confirm && !nameMatches && (
                <div className="text-[10px] text-red-600 mt-1">Doesn't match the exact company name.</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Your password *</Label>
              <Input type="password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-sm mt-1"
                      data-testid="delete-company-password" />
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          rows={2}
                          className="rounded-sm mt-1 text-xs"
                          data-testid="delete-company-reason" />
            </div>
            <div className="text-[11px] text-red-700 bg-red-100 rounded-sm p-2 flex gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div>
                Cool-down <b>24 hours</b>. Restore window <b>30 days total</b>.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" disabled={busy}
                      onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!canSubmit} onClick={submit}
                      className="rounded-sm gap-1.5"
                      data-testid="delete-company-submit">
              <Trash2 className="w-3.5 h-3.5" /> {busy ? "Scheduling…" : "Schedule delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
